import { useEffect, useRef, useState } from "react"

import { App, Modal, Space, Spin, Typography } from "antd"
import cls from "classname"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom"

import ExternallyConnectPaymentProcessorForm from "../../Components/Forms/ExternallyConnectPaymentProcessorForm"
import AcceptInviteModal from "../../Components/Modal/AcceptInviteModal"
import { PrivateRoute } from "../../Components/PrivateRoute"
import {
  DEFLECT_ANALYTICS_ENABLED,
  FEATURE_FLAGS,
  isFeatureUnlocked
} from "../../constants/feature-flags"
import { RoutesPaths } from "../../constants/user-routes"
import { usePhoneCountries } from "../../hooks/usePhoneCountries"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { defaultInnerPage } from "../../Utils/appRoutes"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { adminRoleId, highAccessRoles, superAdminRoleId } from "../../Utils/permission"
import utils, {
  createAccountStepPath,
  onOkShowMessage,
  paymentProcessorMap
} from "../../Utils/utils"
import AdminDisputesPage from "../AdminDisputesPage"
import AdminInvoicesPage from "../AdminInvoicesPage"
import AdminUsersPage from "../AdminUsersPage"
import AlertsPage from "../AlertsPage"
import { AlertDetailsPage } from "../AlertsPage/alertDetailsPage"
import BusinessAccountsPage from "../BusinessAccountsPage"
import BusinessSettingsPage from "../BusinessSettings"
import AdminEnrollments from "../BusinessSettings/AdminEnrollments"
import Business from "../BusinessSettings/Business"
import Pricing from "../BusinessSettings/Pricing"
import { UserBalancePageForAdmin } from "../BusinessSettings/UserBalancePageForAdmin"
import ConfigurePage from "../ConfigurePage"
import AutoRefundPage from "../ConfigurePage/AutoRefund/AutoRefundPage"
import ConnectShopifyStoreVerifyPage from "../ConnectShopifyStoreVerifyPage"
import CreditsPage from "../CreditsPage"
import DashboardPage from "../DashboardPage"
import AlertsByBankPage from "../DashboardPage/subPage/AlertsByBank"
import AlertsByCountryPage from "../DashboardPage/subPage/AlertsByCountry"
import UsersBySourcePage from "../DashboardPage/subPage/UsersBySource"
import DeflectAnalyticsPage from "../DeflectAnalyticsPage"
import DeflectChannelsPage from "../DeflectAnalyticsPage/pages/ChannelsFullPage"
import DeflectCountriesPage from "../DeflectAnalyticsPage/pages/CountriesFullPage"
import DeflectMatchRatePage from "../DeflectAnalyticsPage/pages/MatchRateFullPage"
import DeflectRepeatPage from "../DeflectAnalyticsPage/pages/RepeatCardholdersFullPage"
import DisputesAnalyticsPage from "../DisputesAnalyticsPage"
import DisputesActivityFullPage from "../DisputesAnalyticsPage/pages/ActivityFullPage"
import DisputesCountriesFullPage from "../DisputesAnalyticsPage/pages/CountriesFullPage"
import DisputesReasonCodesFullPage from "../DisputesAnalyticsPage/pages/ReasonCodesFullPage"
import DisputesRepeatDisputersFullPage from "../DisputesAnalyticsPage/pages/RepeatDisputersFullPage"
import DisputesWinRateFullPage from "../DisputesAnalyticsPage/pages/WinRateFullPage"
import DisputesShell from "../DisputesPage/DisputesShell"
import DisputesAllPage from "../DisputesPage/pages/AllPage/AllPage"
import DisputesConfigurePage from "../DisputesPage/pages/ConfigurePage"
import DisputesDetailPage from "../DisputesPage/pages/DetailPage"
import DisputesPreferencesPage from "../DisputesPage/pages/PreferencesPage"
import GettingStartedPage from "../GettingStartedPage"
import CountryOlympicsGuard from "../Health/components/CountryOlympicsGuard"
import HealthMonitoringPage from "../Health/HealthMonitoring"
import HealthOverviewPage from "../Health/HealthOverview"
import MastercardEstimatedProgramStatusPage from "../Health/Mastercard/components/EstimatedProgramStatus/MastercardEstimatedProgramStatusPage"
import BreakdownByCountryPage from "../Health/subPage/BreakdownByCountryPage"
import BreakdownByReasonCodePage from "../Health/subPage/BreakdownByReasonCodePage"
import ChargebackOlympicsPage from "../Health/subPage/ChargebackOlympicsPage"
import EstimatedProgramStatusPage from "../Health/VAMP/components/EstimatedProgramStatus/EstimatedProgramStatusPage"
import HomePage from "../HomePage"
import IntegrationsPage from "../IntegrationsPage"
import CustomLayout from "../Layout"
import LayoutUserPageForAdmin from "../LayoutUserPageForAdmin"
import { updateProfile } from "../LoginPage/redux/auth.actions"
import { logoutThunk } from "../LoginPage/redux/auth.thunks"
import MarketingPage from "../MarketingPage"
import NotFoundPage from "../NotFoundPage"
import PaymentMatchingPage from "../PaymentMatchingPage"
import PerksAdminPage from "../PerksAdminPage"
import PerksPage from "../PerksPage"
import PersonalSettingsPage from "../PersonalSettings"
import EndpointDetailsPage from "../PersonalSettings/PublicAPI/components/EndpointDetailsPage"
import EventDetailsPage from "../PersonalSettings/PublicAPI/components/EventDetailsPage"
import Users from "../PersonalSettings/Users"
import PricingPage from "../PricingPage"
import ReceiptPage from "../ReceiptPage"
import ReceiptConfigureBusinessPage from "../ReceiptPage/ConfigureBusinessPage"
// import DigitalReceiptPage from "../DigitalReceiptPage"
import ReceiptConfigureProfilePage from "../ReceiptPage/ConfigureProfilePage"
import Referrals from "../Referrals"
import EarningsAdminPage from "../Referrals/EarningsAdminPage"
import ReferralsOutsidersLayout from "../Referrals/Outsiders/layout"
import ReferralBAPage from "../Referrals/ReferralBAPage"
import RequestsPage from "../RequestsPage"
import SecurityPage from "../SecurityPage"
import {
  changeNextCreateAccountStep,
  setDemoTourStep,
  userSetProp
} from "../User/redux/user.actions"
import { userActions } from "../User/redux/user.slice"
import {
  connectPaymentProcessor,
  disconnectPaymentProcessorExternally,
  getMaxCreateSteps
} from "../User/redux/user.thunks"
import UsersPage from "../UsersPage"
import { groupParentRoutes, legacyRedirectRoutes } from "./legacyRoutes"

const { Text } = Typography
const appType = process.env.REACT_APP_TYPE

function InternalPages(props) {
  const {
    auth,
    userInfo,
    isAdmin,
    isMobile,
    businessAccounts,
    businessAccountsFetching,
    demoTourStep,
    saveFetching,
    saveResult,
    logout
  } = props

  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const location = useLocation()
  const navigate = useNavigate()
  // const externalDemoMode = useExternalDemoMode()
  const demoRef = useRef(null)
  const helpRef = useRef({ firstLoading: true, firstUserInfoFetched: false })
  const [connectPayment, setConnectPayment] = useState(null)
  usePhoneCountries()
  const connectPaymentName = paymentProcessorMap[connectPayment?.["service_name"]] ?? ""

  let isAuthUserPages = !!auth?.accessToken && !isAdmin
  const isAuthReferralOutsider = isAuthUserPages && userInfo?.referral_outsider
  const isAuthStandardUser = isAuthUserPages && !userInfo?.referral_outsider
  let isAuthAdminPages = !!auth?.accessToken && isAdmin
  // const isRestrictedInvoicesAdmin = userInfo?.role?.id === 5

  const isHighAccessRoleBA = !userInfo?.ba_role || highAccessRoles?.includes(userInfo?.ba_role?.id)
  const isSuperAdmin = userInfo?.role?.id === superAdminRoleId
  const isCommonAdmin = isAdmin && userInfo?.role?.id === adminRoleId
  const isAdminPartner = isAdmin && userInfo?.role?.id === 5

  const isOpenOnboarding = !isOnboardingFinished(userInfo) && !userInfo?.is_created_by_invitation

  let filteredBusinessAccounts = [...businessAccounts]?.filter(
    (ba) => !ba?.["connected_payment_processors"]?.includes(connectPayment?.service_name)
  )

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    if (isAuthReferralOutsider && appType !== "partner") {
      logout()
    }
    if (isAuthStandardUser && appType === "partner") {
      logout()
    }
    if (isAuthAdminPages && appType === "partner") {
      logout()
    }
  }, [isAuthStandardUser, isAuthReferralOutsider, isAuthAdminPages])

  useEffect(() => {
    if (userInfo?.["externally_connect_processor"] && helpRef.current.firstUserInfoFetched) {
      setConnectPayment(userInfo?.["externally_connect_processor"]?.[0])
    }
    helpRef.current.firstUserInfoFetched = true
    if (userInfo?.message && helpRef.current.firstLoading) showMessage(userInfo?.message)
  }, [userInfo])

  useEffect(() => {
    if (userInfo?.id) props.getMaxCreateSteps()
  }, [userInfo?.id])

  useEffect(() => {
    if (["connected", "disconnected"]?.includes(saveResult)) {
      if (connectPayment) {
        let namePayment =
          paymentProcessorMap?.[connectPayment.service_name] ||
          utils.toUpperLatter(connectPayment.service_name)
        let message = null
        let description = null
        if (saveResult === "connected") {
          message = t("Payment processor connected")
          description = t("_namePayment_ was connected successfully.", {
            namePayment
          })
        } else if (saveResult === "disconnected") {
          message = t("Payment processor disconnected")
          description = t("_namePayment_ was disconnected successfully.", {
            namePayment
          })
        }
        notification.success({
          message,
          description,
          ...notificationConfig
        })
        handleCloseModal()
        props.userSaveResultReset()
      }
    }
  }, [saveResult])

  // Tour guide reset on reload
  useEffect(() => {
    if (demoTourStep && userInfo?.is_demo && !userInfo?.is_light_mode) {
      props.setDemoTourStep(1)
      navigate(RoutesPaths.dashboard.root)
    } else {
      props.setDemoTourStep(null)
    }
  }, [])

  const disconnectPayment = (payment) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: t("You have unsaved progress"),
      content: t(
        "Are you sure you want to stop connecting the payment processor? You will lose all your progress and have to start connecting process from scratch."
      ),

      cancelText: t("Cancel"),
      okText: t("Confirm"),
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        danger: true,
        ghost: true
      },
      onOk() {
        props.disconnectPaymentProcessor({
          service_name: payment.service_name,
          ...payment?.["request_params"]
        })
      }
    })
  }

  const showMessage = (message) => {
    if (helpRef.current.firstLoading) helpRef.current.firstLoading = false
    modal.warning({
      ...modalConfig("info"),
      centered: !isMobile,

      title: message?.title,
      content: message?.text,

      okText: t("Got it"),
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        style: { width: "auto" }
      },
      onOk() {
        onOkShowMessage[message.title] &&
          props?.[onOkShowMessage[message.title].action](onOkShowMessage[message.title].value)
        props.clearMessage()
      }
    })
  }

  const handleCloseModal = () => {
    setConnectPayment(null)
    if (saveResult?.["connect_error"]) props.userSaveResultReset()
  }
  const handleCancel = () => {
    disconnectPayment(connectPayment)
  }

  const handleAddNewAccount = () => {
    props.userSaveResultReset()
    props.changeNextCreateAccountStep(1)
    props.setRedirectAfterCreateAccount(location.pathname)
    navigate(createAccountStepPath[1])
  }

  const handleSubmit = (formData) => {
    if (filteredBusinessAccounts.length) {
      const data = {
        ...formData,
        service_name: connectPayment.service_name,
        ...connectPayment?.["request_params"]
      }
      props.connectPaymentProcessor(data)
    } else {
      setConnectPayment(null)
      if (userInfo?.new_onboarding_finished) {
        handleAddNewAccount()
      }
    }
  }

  if (appType === "partner")
    return (
      <Routes>
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthReferralOutsider} />}>
          <Route element={<ReferralsOutsidersLayout />}>
            <Route path="/referrals" element={<Referrals />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    )

  return (
    <>
      <Routes>
        {/* === USER PAGES === */}
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthStandardUser} />}>
          <Route element={<CustomLayout />}>
            {/* Every address the platform had before the product-line scheme, and
                the group parents that open a group's default child (CHA1-2847). */}
            {legacyRedirectRoutes}
            {groupParentRoutes}

            {/* PREVENT */}
            <Route path={RoutesPaths.credits} element={<CreditsPage />} />
            <Route path={`${RoutesPaths.configure.root}/:page`} element={<ConfigurePage />} />
            <Route
              path={RoutesPaths.configure.root}
              element={<Navigate to={RoutesPaths.configure.enrollment} replace />}
            />
            {/* Developers is a Settings section (CHA1-2847); Team is one too, and
                both keep the role gate they had as standalone pages. The webhook
                detail screens are too deep for the `settings/:hash/:action`
                pattern, so they get their own routes. */}
            <Route
              path={`${RoutesPaths.settings.developers_webhooks}/endpoint/details`}
              element={<EndpointDetailsPage />}
            />
            <Route
              path={`${RoutesPaths.settings.developers_webhooks}/endpoints/:endpoint_id`}
              element={<EndpointDetailsPage />}
            />
            <Route
              path={`${RoutesPaths.settings.developers_webhooks}/events/:event_id`}
              element={<EventDetailsPage />}
            />

            <Route path="business_settings/:hash/:id" element={<BusinessSettingsPage />} />
            <Route
              path={`${RoutesPaths.settings.root}/:hash/:action`}
              element={<PersonalSettingsPage balance />}
            />
            <Route path="connect-shopify/verify" element={<ConnectShopifyStoreVerifyPage />} />

            {/* DEFLECT — RECEIPT */}
            {/* Same gate the sidebar uses to lock the Receipt block (`unlockFlag`):
                without an explicit `true` the pages aren't reachable by URL either. */}
            <Route
              element={
                <PrivateRoute
                  redirectPath={RoutesPaths.dashboard.root}
                  auth={isFeatureUnlocked(userInfo, FEATURE_FLAGS.RECEIPTS)}
                />
              }
            >
              <Route path={RoutesPaths.receipts.all} element={<ReceiptPage />} />
              {DEFLECT_ANALYTICS_ENABLED && (
                <>
                  <Route path={RoutesPaths.deflect.analytics} element={<DeflectAnalyticsPage />} />
                  <Route
                    path={RoutesPaths.deflect.analytics_match_rate}
                    element={<DeflectMatchRatePage />}
                  />
                  <Route
                    path={RoutesPaths.deflect.analytics_countries}
                    element={<DeflectCountriesPage />}
                  />
                  <Route
                    path={RoutesPaths.deflect.analytics_repeat}
                    element={<DeflectRepeatPage />}
                  />
                  <Route
                    path={RoutesPaths.deflect.analytics_channels}
                    element={<DeflectChannelsPage />}
                  />
                </>
              )}
              <Route
                path={RoutesPaths.receipts.configure_business}
                element={<ReceiptConfigureBusinessPage />}
              />
              {/*<Route*/}
              {/*  element={*/}
              {/*    <PrivateRoute*/}
              {/*      redirectPath={RoutesPaths.receipts.configure_business}*/}
              {/*      auth={isHighAccessRoleBA}*/}
              {/*    />*/}
              {/*  }*/}
              {/*>*/}
              <Route
                path={RoutesPaths.receipts.configure_profile}
                element={<ReceiptConfigureProfilePage />}
              />
              <Route
                path={RoutesPaths.receipts.configure_profile_hash(":section")}
                element={<ReceiptConfigureProfilePage />}
              />
              {/*</Route>*/}
              <Route
                path={RoutesPaths.receipts._configure}
                element={<Navigate to={RoutesPaths.receipts.configure_profile} replace />}
              />
            </Route>
            {/*<Route path="receipts/configure/business" element={<ReceiptConfigurePage />} />*/}

            {/* isHighAccessRoleBA */}
            <Route
              element={<PrivateRoute redirectPath={defaultInnerPage} auth={isHighAccessRoleBA} />}
            >
              <Route
                element={
                  <PrivateRoute
                    redirectPath={RoutesPaths.dashboard.root}
                    auth={userInfo?.is_business_health_overview_allowed}
                  />
                }
              >
                <Route path={RoutesPaths.health.overview} element={<HealthOverviewPage />} />
                <Route
                  path={`${RoutesPaths.health.overview}/chargeback_olympics`}
                  element={
                    <CountryOlympicsGuard>
                      <ChargebackOlympicsPage />
                    </CountryOlympicsGuard>
                  }
                />
                <Route
                  path={`${RoutesPaths.health.overview}/breakdown_by_country`}
                  element={
                    <CountryOlympicsGuard>
                      <BreakdownByCountryPage />
                    </CountryOlympicsGuard>
                  }
                />
                <Route
                  path={`${RoutesPaths.health.overview}/breakdown_by_reason_code`}
                  element={<BreakdownByReasonCodePage />}
                />
              </Route>
              <Route
                element={
                  <PrivateRoute
                    redirectPath={RoutesPaths.dashboard.root}
                    auth={userInfo?.is_business_health_allowed}
                  />
                }
              >
                <Route path={RoutesPaths.health.monitoring} element={<HealthMonitoringPage />} />
                <Route
                  path={`${RoutesPaths.health.monitoring}/estimated_program_status`}
                  element={<EstimatedProgramStatusPage />}
                />
                <Route
                  path={`${RoutesPaths.health.monitoring}/mastercard_estimated_program_status`}
                  element={<MastercardEstimatedProgramStatusPage />}
                />
              </Route>
            </Route>
            <Route path={RoutesPaths.home} element={<HomePage />} />
            <Route
              path="/getting_started"
              element={
                <PrivateRoute redirectPath={RoutesPaths.dashboard.root} auth={isOpenOnboarding}>
                  <GettingStartedPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/getting_started/new-business"
              element={
                // <PrivateRoute redirectPath={RoutesPaths.dashboard.root} auth={isOpenOnboarding} >
                <GettingStartedPage />
                // </PrivateRoute>
              }
            />
          </Route>
        </Route>

        {/* === ADMIN PAGES === */}
        <Route element={<PrivateRoute redirectPath="/login" auth={isAuthAdminPages} />}>
          <Route element={<CustomLayout />}>
            <Route
              path="admin/dashboard/alerts_by_country"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsByCountryPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route path="dashboard/users_by_source" element={<UsersBySourcePage />} />
            <Route
              path="admin/dashboard/alerts_by_bank"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsByBankPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/dashboard"
              element={
                <LayoutUserPageForAdmin>
                  <DashboardPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/alerts"
              element={
                <LayoutUserPageForAdmin>
                  <AlertsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/alerts/details/:alert_id"
              element={
                <LayoutUserPageForAdmin>
                  <AlertDetailsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/integrations"
              element={
                <LayoutUserPageForAdmin>
                  <IntegrationsPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route
              path="admin/receipts"
              element={
                <LayoutUserPageForAdmin>
                  <ReceiptPage usersViewForAdmin />
                </LayoutUserPageForAdmin>
              }
            />
            <Route path="admin/disputes" element={<AdminDisputesPage />} />
            <Route path="admin/perks" element={<PerksAdminPage />} />
            <Route path="admin/payment_matching" element={<PaymentMatchingPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route element={<PrivateRoute redirectPath="/users" auth={isSuperAdmin} />}>
              <Route path="admin_users" element={<AdminUsersPage />} />
            </Route>
            <Route path="users/user_balance" element={<UserBalancePageForAdmin />} />
            <Route path="business_accounts" element={<BusinessAccountsPage />} />
            <Route path="referral" element={<Referrals />} />
            <Route path="referral/affiliate/:id" element={<EarningsAdminPage />} />
            <Route path="referral/referral-ba/:id" element={<ReferralBAPage />} />
            <Route element={<PrivateRoute redirectPath={RoutesPaths.alerts.all} auth={isAdmin} />}>
              <Route path="pricing" element={<PricingPage />} />
            </Route>
            <Route
              element={
                <PrivateRoute redirectPath={RoutesPaths.alerts.all} auth={!isAdminPartner} />
              }
            >
              <Route path="invoices" element={<AdminInvoicesPage />} />
            </Route>
            <Route
              element={
                <PrivateRoute
                  redirectPath={RoutesPaths.alerts.all}
                  auth={!isCommonAdmin && !isAdminPartner}
                />
              }
            >
              <Route path="marketing" element={<MarketingPage />} />
            </Route>
            <Route path="requests/:hash" element={<RequestsPage />} />
            <Route path="business_settings" element={<BusinessSettingsPage />}>
              <Route path="enrollment" element={<AdminEnrollments />} />
              <Route path="auto-refund" element={<AutoRefundPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="pricing" element={<Pricing />} />
              <Route path="business" element={<Business />} />
              <Route path="users" element={<Users isBusinessSettingsPage />} />
              <Route path="receipts/all" element={<ReceiptPage usersViewForAdmin />} />
              <Route
                path="receipts/configure"
                element={<ReceiptConfigureBusinessPage usersViewForAdmin />}
              />
              {/* Support's read-only view of the merchant's Profile wizard
                  (CHA1-1975 BLOCK 2). Unlike the merchant's own route this one
                  is not gated on a BA role — support reaches it as an admin. */}
              <Route
                path="receipts/configure/profile"
                element={<ReceiptConfigureProfilePage usersViewForAdmin />}
              />
              <Route
                path="receipts/configure/profile/:section"
                element={<ReceiptConfigureProfilePage usersViewForAdmin />}
              />
              <Route path="business_health/overview" element={<HealthOverviewPage />} />
              <Route
                path="business_health/overview/chargeback_olympics"
                element={
                  <CountryOlympicsGuard>
                    <ChargebackOlympicsPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="business_health/overview/breakdown_by_country"
                element={
                  <CountryOlympicsGuard>
                    <BreakdownByCountryPage />
                  </CountryOlympicsGuard>
                }
              />
              <Route
                path="business_health/overview/breakdown_by_reason_code"
                element={<BreakdownByReasonCodePage />}
              />
              <Route path="business_health/monitoring" element={<HealthMonitoringPage />} />
              <Route
                path="business_health/monitoring/estimated_program_status"
                element={<EstimatedProgramStatusPage />}
              />
              <Route
                path="business_health/monitoring/mastercard_estimated_program_status"
                element={<MastercardEstimatedProgramStatusPage />}
              />
            </Route>
          </Route>
        </Route>

        {/* === SHARED PAGES (USER OR ADMIN) === */}
        <Route
          element={
            <PrivateRoute redirectPath="/login" auth={isAuthStandardUser || isAuthAdminPages} />
          }
        >
          <Route element={<CustomLayout />}>
            <Route
              element={<PrivateRoute redirectPath={RoutesPaths.alerts.all} auth={!isCommonAdmin} />}
            >
              <Route
                path={RoutesPaths.dashboard.alerts_by_country}
                element={<AlertsByCountryPage />}
              />
              <Route path={RoutesPaths.dashboard.alerts_by_bank} element={<AlertsByBankPage />} />
              <Route
                path={RoutesPaths.dashboard.root}
                element={<DashboardPage demoRef={demoRef} />}
              />
              <Route
                path={RoutesPaths.dashboard.thank_you}
                element={<DashboardPage demoRef={demoRef} />}
              />
            </Route>
            <Route path={RoutesPaths.alerts.all} element={<AlertsPage />} />
            <Route
              path={`${RoutesPaths.alerts._details}/:alert_id`}
              element={<AlertDetailsPage />}
            />
            <Route
              element={
                <PrivateRoute
                  redirectPath={RoutesPaths.dashboard.root}
                  auth={!!userInfo?.show_perks}
                />
              }
            >
              <Route path={RoutesPaths.perks} element={<PerksPage />} />
            </Route>
            {/* Recover (dispute management). Every page renders inside DisputesShell,
                which picks the activated / deactivated / never-set-up experience from the
                activation payload — except Analytics and its five "View All" pages while their
                endpoints are mocks (ANALYTICS_MOCKED): the shell gates on the account's real
                profile and disputes, which would hide the mocked pages. There is deliberately
                no /recover/details/edit/:id: the evidence editor was removed from the product
                (RCV-5-36). */}
            <Route
              path={RoutesPaths.recover.all}
              element={
                <DisputesShell>
                  <DisputesAllPage />
                </DisputesShell>
              }
            />
            <Route
              path={RoutesPaths.recover.configure}
              element={
                <DisputesShell>
                  <DisputesConfigurePage />
                </DisputesShell>
              }
            />
            <Route
              path={RoutesPaths.recover.configure_section(":section")}
              element={
                <DisputesShell>
                  <DisputesConfigurePage />
                </DisputesShell>
              }
            />
            <Route
              path={RoutesPaths.recover.details(":dispute_id")}
              element={
                <DisputesShell>
                  <DisputesDetailPage />
                </DisputesShell>
              }
            />
            <Route path={RoutesPaths.recover.analytics} element={<DisputesAnalyticsPage />} />
            <Route
              path={RoutesPaths.recover.analytics_countries}
              element={<DisputesCountriesFullPage />}
            />
            <Route
              path={RoutesPaths.recover.analytics_activity}
              element={<DisputesActivityFullPage />}
            />
            <Route
              path={RoutesPaths.recover.analytics_reason_codes}
              element={<DisputesReasonCodesFullPage />}
            />
            <Route
              path={RoutesPaths.recover.analytics_repeat_disputers}
              element={<DisputesRepeatDisputersFullPage />}
            />
            <Route
              path={RoutesPaths.recover.analytics_win_rate}
              element={<DisputesWinRateFullPage />}
            />
            <Route
              path={RoutesPaths.recover.preferences}
              element={
                <DisputesShell>
                  <DisputesPreferencesPage />
                </DisputesShell>
              }
            />
            <Route path="business_settings/:hash" element={<BusinessSettingsPage />} />
            <Route path={`${RoutesPaths.settings.root}/:hash`} element={<PersonalSettingsPage />} />
          </Route>
        </Route>

        <Route
          path={RoutesPaths.settings.root}
          element={<PrivateRoute redirectPath={RoutesPaths.settings.my_profile} auth={false} />}
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={!!connectPayment && userInfo?.new_onboarding_finished}
        centered={!isMobile}
        width={442}
        title={
          <Space direction="vertical">
            <Text className="payment-connect-title">
              {t("_namePayment_ connect", { namePayment: connectPaymentName })}
            </Text>
            <Text className="payment-connect-sub_title">
              {filteredBusinessAccounts?.length
                ? t(
                    "Which business account do you want to connect to your current _namePayment_ account?",
                    { namePayment: connectPaymentName }
                  )
                : businessAccountsFetching
                  ? ""
                  : `No available business accounts to connect ${connectPaymentName}.`}
            </Text>
          </Space>
        }
        footer={null}
        closeIcon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        closable="true"
        onCancel={handleCancel}
        destroyOnHidden
        loading={businessAccountsFetching}
        classNames={{
          content: "clear-bg full-width-header payment-connect",
          header: cls("payment-connect", [connectPayment?.["service_name"]])
        }}
      >
        <Spin
          size="large"
          //spinning={fetching}
          spinning={false}
          wrapperClassName="visible"
        >
          <ExternallyConnectPaymentProcessorForm
            onClose={handleCancel}
            onSubmit={handleSubmit}
            businessAccounts={filteredBusinessAccounts}
            businessAccountsFetching={businessAccountsFetching}
            error={saveResult?.["connect_error"]}
            paymentProcessorName={connectPaymentName}
            loading={saveFetching}
          />
        </Spin>
      </Modal>

      <AcceptInviteModal />
    </>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,

  businessAccounts: state.user.businessAccounts,
  businessAccountsFetching: state.user.businessAccountsFetching,
  saveFetching: state.user.saveFetching,
  saveResult: state.user.saveResult,
  demoTourStep: state.user.demoTourStep
})

const mapDispatchToProps = (dispatch) => ({
  connectPaymentProcessor: (data) => dispatch(connectPaymentProcessor(data)),
  disconnectPaymentProcessor: (data) => dispatch(disconnectPaymentProcessorExternally(data)),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),

  changeNextCreateAccountStep: (nextCreateAccountStep) =>
    dispatch(changeNextCreateAccountStep(nextCreateAccountStep)),
  setDemoTourStep: (step) => dispatch(setDemoTourStep(step)),
  getMaxCreateSteps: () => dispatch(getMaxCreateSteps()),
  setRedirectAfterCreateAccount: (location) =>
    dispatch(userSetProp({ key: "redirectAfterCreateAccount", value: location })),
  clearMessage: () => dispatch(updateProfile({ message: null })),
  logout: () => dispatch(logoutThunk())
})

InternalPages.propTypes = {
  auth: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    isValidRefreshToken: PropTypes.bool
  }),
  userInfo: PropTypes.shape({
    onboarding_finished: PropTypes.bool
  }),
  isAdmin: PropTypes.bool
}

export default connect(mapStateToProps, mapDispatchToProps)(InternalPages)
